import { useState, useEffect, useCallback } from "react";
import { collection, onSnapshot, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { db } from "../_db";

interface UseDataOptions {
  collectionName: string;
  documentId?: string;
}

interface UseDataWithFilterOptions {
  collectionName: string;
  filters?: Array<{
    field: string;
    operator: "==" | "!=" | "<" | "<=" | ">" | ">=" | "array-contains" | "in" | "array-contains-any" | "not-in";
    value: any;
  }>;
}

export const useData = <T extends { id: string }>({ collectionName, documentId }: UseDataOptions) => {
  const [data, setData] = useState<T[]>([]);
  const [document, setDocument] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (documentId) {
        // Get a single document
        const docRef = doc(db, collectionName, documentId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const docData = {
            id: docSnap.id,
            ...docSnap.data(),
          } as T;
          setDocument(docData);
          setData([docData]);
        } else {
          setDocument(null);
          setData([]);
        }
      } else {
        // Get all documents
        const querySnapshot = await getDocs(collection(db, collectionName));
        const initialData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as T[];

        setData(initialData);
      }
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
    }
  }, [collectionName, documentId]);

  // Function to manually refetch data
  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupRealTimeListener = () => {
      try {
        if (documentId) {
          // Set up real-time listener for a single document
          unsubscribe = onSnapshot(
            doc(db, collectionName, documentId),
            (snapshot) => {
              if (snapshot.exists()) {
                const docData = {
                  id: snapshot.id,
                  ...snapshot.data(),
                } as T;
                setDocument(docData);
                setData([docData]);
              } else {
                setDocument(null);
                setData([]);
              }
              setLoading(false);
            },
            (err) => {
              setError(err);
              setLoading(false);
            }
          );
        } else {
          // First fetch initial data
          fetchData();

          // Then set up real-time listener for all documents
          unsubscribe = onSnapshot(
            collection(db, collectionName),
            (snapshot) => {
              const updatedData = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              })) as T[];
              setData(updatedData);
            },
            (err) => {
              setError(err);
              setLoading(false);
            }
          );
        }
      } catch (err) {
        setError(err as Error);
        setLoading(false);
      }
    };

    setupRealTimeListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [collectionName, documentId, fetchData]);

  return { data, document, loading, error, refetch };
};

// Hook for filtered queries
export const useDataWithFilter = <T extends { id: string }>({ collectionName, filters }: UseDataWithFilterOptions) => {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      let queryRef = collection(db, collectionName);

      // Apply filters if provided
      if (filters && filters.length > 0) {
        let q = query(queryRef);
        filters.forEach((filter) => {
          q = query(q, where(filter.field, filter.operator, filter.value));
        });

        const querySnapshot = await getDocs(q);
        const initialData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as T[];

        setData(initialData);
      } else {
        setData([]);
      }
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
    }
  }, [collectionName, JSON.stringify(filters)]); // Use JSON.stringify for deep comparison

  // Function to manually refetch data
  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
};
